// Métadonnées par CLI + point d'extension `customTabs`.
//
// Chaque CLI peut déclarer des tabs additionnels au-delà des 5 tabs de base
// (Sessions / Terminal / Messages / Workspaces / Git). Les custom tabs reçoivent
// un contexte { session, stream, client } pour rendre une UI spécifique.
//
// Phase 2 livrée : custom tabs additifs (Plan pour Claude, Steer pour Codex,
// Approbations pour Kimi, Sessions natives pour tous). Sources d'inspiration :
// /Volumes/ssd-portable/agent-os-2/extras/aight-analysis/17-abstraction-design.md.
import type { JSX } from "solid-js";
import type { useToolClient } from "@/shared/tool-client";
import type { CliStream } from "./stream";
import type { CliSession } from "./types";
import { makePlanTab, makeSteerTab, makeApprovalsTab, makeNativeSessionsTab } from "./custom-tabs";

export interface CliCustomTabContext {
  /** Session active (null si aucune sélectionnée). */
  session: () => CliSession | null;
  /** Flux live associé à la session active. */
  stream: CliStream;
  /** Client d'outils partagé. */
  client: ReturnType<typeof useToolClient>;
}

export interface CliCustomTab {
  id: string;
  label: string;
  render: (ctx: CliCustomTabContext) => JSX.Element;
}

export interface CliConfig {
  cli: string;
  label: string;
  title: string;
  /** Couleur d'accent (optionnelle, pour différencier les panels). */
  accent?: string;
  /** Texte d'aide affiché si le CLI n'est pas installé. */
  installHint?: string;
  /** Tabs additionnels spécifiques à ce CLI (phase 2). */
  customTabs?: CliCustomTab[];
}

export const CLI_CONFIG: Record<string, CliConfig> = {
  claude: {
    cli: "claude",
    label: "Claude",
    title: "Claude Code",
    accent: "#d97757",
    installHint: "Installez Claude Code : npm i -g @anthropic-ai/claude-code",
    customTabs: [makePlanTab("claude"), makeNativeSessionsTab("claude")],
  },
  codex: {
    cli: "codex",
    label: "Codex",
    title: "OpenAI Codex",
    accent: "#10a37f",
    installHint: "Installez Codex : npm i -g @openai/codex",
    customTabs: [makeSteerTab(), makeNativeSessionsTab("codex")],
  },
  kimi: {
    cli: "kimi",
    label: "Kimi",
    title: "Kimi Code",
    accent: "#7c3aed",
    installHint: "Installez Kimi Code : npm i -g @anthropic-ai/kimi-code",
    customTabs: [makeApprovalsTab(), makeNativeSessionsTab("kimi")],
  },
  gemini: {
    cli: "gemini",
    label: "Gemini",
    title: "Gemini CLI",
    accent: "#4285f4",
    installHint: "Installez Gemini CLI : npm i -g @google/gemini-cli",
    customTabs: [makeNativeSessionsTab("gemini")],
  },
  opencode: {
    cli: "opencode",
    label: "OpenCode",
    title: "OpenCode",
    accent: "#f59e0b",
    installHint: "Installez OpenCode : npm i -g opencode-ai",
    customTabs: [makeNativeSessionsTab("opencode")],
  },
  hermes: {
    cli: "hermes",
    label: "Hermes",
    title: "Hermes",
    accent: "#0ea5e9",
    installHint: "Installez Hermes : pip install hermes-cli",
    customTabs: [makeNativeSessionsTab("hermes")],
  },
  openclaw: {
    cli: "openclaw",
    label: "OpenClaw",
    title: "OpenClaw",
    accent: "#ef4444",
    installHint: "Installez OpenClaw selon sa doc officielle.",
    customTabs: [makeNativeSessionsTab("openclaw")],
  },
};

/** Récupère la config d'un CLI (throw si inconnu — défini au boot). */
export function getCliConfig(cli: string): CliConfig {
  const cfg = CLI_CONFIG[cli];
  if (!cfg) throw new Error(`CLI inconnu : « ${cli} ». Ajoutez-le à cli/cli-config.ts.`);
  return cfg;
}
